import React, {useEffect, useRef, useState} from "react";
import {Route, Switch, useHistory, useLocation, useParams} from "react-router-dom";
import {RouteWithId} from "../routes/types";
import ReviewFiduciariesForm from "./pages/ReviewFiduciaries/ReviewFiduciariesForm";
import EstateFlowchart from "./components/EstateFlowchart/EstateFlowchart";
import RevocableTrustForm from "./forms/RevocableTrustForm/RevocableTrustForm";
import {MemberGroup} from "../ClientManagement/models/InvestorGroupType";
import LoadingIndicator from "../pages/LoadingIndicator";
import {
    genericErrorModalSelector,
    resetEstateFlowchartState,
    resetFlowchartState,
    resetFlowchartStateAfterResourcesNav,
    selectFlowchartIdAfterResourcesNav,
    setEstateFlowchartState,
    setFamilyMembers,
    setGenericErrorModal,
    setListOfStates,
    setSelectedFlowchartId,
} from "./slices/EstateFlowChartSlice";
import {EstateFlowchartState, FiduciarySummary} from "./model/EstateFlowchartTypes";
import {estateFlowchartApiClient, ResponseStatus} from "./client/EstateFlowchartApiClient";
import {ReferenceDataType} from "../models/referenceData/ReferenceDataType";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import IrrevocableTrustForm from "./forms/IrrevocableTrustForm/IrrevocableTrustForm";
import WillForm from "./forms/WillForm/WillForm";
import MiscellaneousForm from "./forms/MiscellaneousForm/MiscellaneousForm";
import GenericErrorModal, {
    genericEmptyErrorModalData,
    GenericErrorModalData
} from "../components/Modal/Error/GenericErrorModal";
import {EmptyStateContainer} from "../components";
import {setFiduciarySummaryState} from "./slices/FiduciarySlice";
import useAnalytics from "../utils/useAnalytics";
import {selectReleaseToggles} from "../ReleaseToggles/releaseTogglesSlice";

const EstateFlowchartMain = () => {
    const {id} = useParams<RouteWithId>();
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<GenericErrorModalData>(genericEmptyErrorModalData);
    const [showTempErrorMessage, setShowTempErrorMessage] = useState(false);
    const currentLocation = useLocation();
    const genericError = useAppSelector(genericErrorModalSelector);
    const history = useHistory();
    const locationRef = useRef(history.location);
    const selectedFlowchartIdAfterResourcesNav = useAppSelector(selectFlowchartIdAfterResourcesNav);
    const enableEstateFlowchartMVPRelease = useAppSelector(selectReleaseToggles)?.enableEstateFlowchartMVPRelease;

    const openErrorModal = (e: ResponseStatus) => {
        setError({
            isOpen: true,
            header: "Unable to Load This Page",
            message: (
                <>
                    <p>Check your VPN connection and retry.</p>
                    <p>If the problem persists, contact <a href="mailto:GPIITSupport@ntrs.com">GPS Technical
                        Support</a> at GPIITSupport@ntrs.com and provide the following details:</p>
                </>
            ),
            profileId: id,
            time: new Date(),
            errorDetail: `Failed to load Estate Flowchart page (${e.statusCode})`,
            operationId: e.traceId
        })
    }

    const closeErrorModal = () => {
        setError({...error, isOpen: false});
    };

    const fetchAll = () => {
        setLoading(true);

        const call1: Promise<ResponseStatus> = estateFlowchartApiClient.getFlowcharts(id);
        const call2: Promise<ResponseStatus> = estateFlowchartApiClient.getMemberGroup(id);
        const call3: Promise<ResponseStatus> = estateFlowchartApiClient.getReferenceData();
        const call4: Promise<ResponseStatus> = estateFlowchartApiClient.getFiduciaries(id);

        Promise.all([call1, call2, call3, call4])
            .then(res => {
                if (!res[3].isSuccessStatusCode) {
                    throw new Error(JSON.stringify(res[3]));
                }
                processFlowcharts(res[0].data);
                processFamilyMembers(res[1].data);
                processStates(res[2].data);
                processFiduciaries(res[3].data);
            })
            .catch(err => {
                openErrorModal(JSON.parse(err.message));
            })
            .finally(() => {
                setLoading(false)
            });
    }

    const processFlowcharts = (flowcharts: EstateFlowchartState[]) => {
        const urlFlowchartId = currentLocation.pathname.split("/").find((segment: string, index: number, array: string[]) => {
            return array[index - 1] === "EstateFlowchart";
        });

        if (flowcharts && flowcharts.length > 0) {
            dispatch(setEstateFlowchartState(flowcharts));
            const currentFlowchartId = urlFlowchartId ??
                (enableEstateFlowchartMVPRelease ? selectedFlowchartIdAfterResourcesNav: undefined) ??
                flowcharts[0]?.flowchartId;
            dispatch(setSelectedFlowchartId(currentFlowchartId));
        } else {
            dispatch(resetEstateFlowchartState());
        }
    }

    const processFamilyMembers = (memberGroup: MemberGroup) => {
        dispatch(setFamilyMembers(memberGroup))
    }

    const processStates = (referenceData: ReferenceDataType) => {
        dispatch(setListOfStates(referenceData.statesOfResidency));
    }

    const processFiduciaries = (fiduciaries: FiduciarySummary) => {
        dispatch(setFiduciarySummaryState(fiduciaries))
    }

    const handleRetryClickInCommunicationErrorModal = () => {
        closeErrorModal();
        fetchAll();
    }

    const handleCloseClickInCommunicationErrorModal = () => {
        closeErrorModal();
        setShowTempErrorMessage(true);
    }

    useAnalytics(
        "Navigate_EstateFlowchart",
        "Navigate_EstateFlowchart_TimeSpent",
    );

    useEffect(() => {
        locationRef.current = history.location;

        return () => {
            const nextPath = history.location.pathname;
            if (nextPath.includes("/Resources") && enableEstateFlowchartMVPRelease) {
                dispatch(resetFlowchartStateAfterResourcesNav())
            } else {
                dispatch(resetFlowchartState());
            }
        };
    }, []);

    useEffect(() => {
        fetchAll();
    }, [id]);

    if (loading) {
        return <LoadingIndicator/>;
    }

    const handleErrorCloseButton = () => {
        dispatch(setGenericErrorModal({
            isOpen: false,
            operationId: ""
        }));
    }

    if (error.isOpen) {
        return (
            <GenericErrorModal
                errorModalData={error}
                onClickButton={handleRetryClickInCommunicationErrorModal}
                onRequestClose={handleCloseClickInCommunicationErrorModal}
                buttonText="Retry"
                buttonProps={
                    {
                        primary: true,
                        className: 'full-width center-align',
                        iconPosition: 'left',
                        iconName: 'refresh'
                    }
                }
                showAlertIcon={false}
            />
        )
    }

    if (showTempErrorMessage) {
        return (
            <EmptyStateContainer
                className="no-wpo-summary-placeholder"
                title="Unable to Load This Page"
                size="large"
                description="Try again later."
            />
        )
    }

    return (
        <>
            {genericError && <GenericErrorModal
                errorModalData={{
                    ...genericError,
                    header: "Communication Failure",
                    message: "We have experienced a communication failure. If this error remains, please contact the help desk."
                }}
                onClickButton={handleErrorCloseButton}
                buttonText={'Close'}
            />}
            <Switch>
                <Route exact path="/Profile/:id/ClientProfile/EstateFlowchart">
                    <div className="estate-flowchart-form-shell">
                        <EstateFlowchart profileId={id}/>
                    </div>
                </Route>
                <Route path="/Profile/:id/ClientProfile/EstateFlowchart/ReviewFiduciaries">
                    <div className="estate-flowchart-form-shell">
                        <ReviewFiduciariesForm/>
                    </div>
                </Route>
                <Route path="/Profile/:id/ClientProfile/EstateFlowchart/:flowchartId/RevocableTrust/:trustId">
                    <div className="estate-flowchart-form-shell">
                        <RevocableTrustForm/>
                    </div>
                </Route>
                <Route path="/Profile/:id/ClientProfile/EstateFlowchart/:flowchartId/IrrevocableTrust/:trustId">
                    <div className="estate-flowchart-form-shell">
                        <IrrevocableTrustForm/>
                    </div>
                </Route>
                <Route path="/Profile/:id/ClientProfile/EstateFlowchart/:flowchartId/Will/:trustId">
                    <div className="estate-flowchart-form-shell">
                        <WillForm/>
                    </div>
                </Route>
                <Route path="/Profile/:id/ClientProfile/EstateFlowchart/:flowchartId/Miscellaneous/:trustId">
                    <div className="estate-flowchart-form-shell">
                        <MiscellaneousForm />
                    </div>
                </Route>
            </Switch>
        </>
    )
}

export default EstateFlowchartMain;
